import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { myFetch } from '@commons/fetch';
import { notifications } from '@mantine/notifications';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Loader,
  Title,
} from '@mantine/core';
import { CustomInput } from '@components/CustomInput';
import { useUser } from '@contexts/UserContext';
import { useAppDispatch } from '@store';
import { addFolder } from '@store/slices/foldersSlice';

export const UserDocumentCreate = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { username, modelId } = useParams<{
    username: string;
    modelId: string;
  }>();
  const intl = useIntl();
  const [model, setModel] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchModel = async () => {
      try {
        setIsLoading(true);
        const response = await myFetch(`/${username}/models/${modelId}`);
        setModel(response.data);
      } catch (error) {
        notifications.show({
          title: intl.formatMessage({ id: 'error' }),
          message: intl.formatMessage({ id: 'errorFetchingModel' }),
          color: 'red',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchModel();
  }, [username, modelId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const response = await myFetch(`/${username}/folders/files`, {
        method: 'POST',
        body: JSON.stringify({
          category: model.category || 'UNKNOWN',
          file: {
            fields: model.fields,
            name: model.name,
            type: model.type,
            template: model.template,
          },
        }),
      });

      // Add the newly created folder to the Redux store
      // The API returns { success: true, data: folder } where folder contains the new file
      if (response.data) {
        dispatch(addFolder(response.data));
      }

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'fileCreatedInANewFolder' },
          { name: model.name }
        ),
        color: 'green',
      });

      navigate(-1);
    } catch (error: any) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !model) {
    return (
      <Container size="xl">
        <Center h={250}>
          <Loader />
        </Center>
      </Container>
    );
  }

  return (
    <Container my="md" size="sm" mb={200}>
      <Title order={3} my="md">
        {model?.name}
      </Title>

      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap="md">
          {model.fields.map((field: any, index: number) => (
            <Box
              key={`${field.name}-${index}`}
              style={{
                border: [
                  'signature',
                  'divider',
                  'title',
                  'disclaimer',
                ].includes(field.type)
                  ? 'none'
                  : '1px solid var(--mantine-color-default-border)',
                padding: [
                  'signature',
                  'divider',
                  'title',
                  'disclaimer',
                ].includes(field.type)
                  ? '0'
                  : '10px',
                borderRadius: 'var(--mantine-radius-sm)',
              }}
            >
              <CustomInput
                key={`${field.name}-${index}`}
                label={field.label}
                name={field.name}
                type={field.type}
                description={field.description || ''}
                items={field.items || []}
                isDisabled={user ? false : field.isDisabled ? true : false}
                isReadonly={field.isReadonly || false}
                isRequired={field.isRequired || false}
                availableImages={field.availableImages}
                maxSelection={field.maxSelection}
                inputs={field.inputs}
                variant={field.variant}
                diacritics={field.diacritics}
                autoCorrect={field.autoCorrect}
                dateMinValue={field.dateMinValue}
                customInput={field.customInput}
                value={field.value}
                title={field.title}
                onChange={(value: any) => {
                  setModel({
                    ...model,
                    fields: model.fields.map((f: any) =>
                      f.name === field.name
                        ? {
                            ...f,
                            value: value,
                          }
                        : f
                    ),
                  });
                }}
              />
            </Box>
          ))}
        </Flex>

        <Button type="submit" variant="primary" fullWidth my="md">
          {intl.formatMessage({ id: 'create' })}
        </Button>
      </form>
    </Container>
  );
};
